import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, Grid, Typography, Button, Checkbox, Autocomplete, TextField, Chip, IconButton, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import api from "../../../api/client";
import QuestionPaperTemplate from "../anna-university-template/QuestionPaperTemplate";
import { buildPaperFromSections } from "./paperBuilder";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

const QUESTION_TYPE_OPTIONS = ["MCQ", "Descriptive", "Fill-in-the-blanks"];
const LEVEL_OPTIONS = ["Beginner", "Intermediate", "Advanced"];
const PAGE_SIZE = 20;

const tagBase = { height: "20px", fontSize: "10px", fontWeight: 700, borderRadius: "8px" };
const TAG = {
  type: { ...tagBase, backgroundColor: "#e3f2fd", color: "#1c97e3" },
  level: { ...tagBase, backgroundColor: "#fef3c7", color: "#d97706" },
  marks: { ...tagBase, backgroundColor: "#ede9fe", color: "#7c3aed" },
  bloom: { ...tagBase, backgroundColor: "#dcfce7", color: "#16a34a" },
  sub: { ...tagBase, backgroundColor: "#eef1f5", color: "#5a6473" },
  inPaper: { ...tagBase, backgroundColor: "#e2e8f0", color: "#475569" },
};
const commonTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#d1d1d1" },
    "&:hover fieldset": { borderColor: "#a8a8a8" },
    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
  },
};
const colSx = { p: 2, borderRadius: "12px", border: "1px solid #f1f3f6", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" };

// list -> count questions; either-or -> count pairs (2 questions each).
const sectionRequires = (s) => (s.pattern === "either-or" ? (s.count || 0) * 2 : s.count || 0);

const AddQuestionsToPaper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const assessmentId = location.state?.assessment_id || null;
  const headerData = useMemo(() => location.state?.header || {}, [location.state]);

  // sections enriched with the questions the teacher has added (tracked client-side
  // from AddQuestionsToSection responses, since there is no get-section-questions API).
  const [sections, setSections] = useState(() => {
    const templateParts = location.state?.template_parts || [];
    // Questions already added inline on the create page, keyed by paper_section_id.
    const prefilled = location.state?.prefilled || {};
    return (location.state?.sections || []).map((s, i) => {
      const tp = templateParts.find((p) => p.title === s.section_name) || templateParts[i] || {};
      return {
        paper_section_id: s.paper_section_id,
        title: s.section_name,
        pattern: s.pattern,
        count: s.count,
        marks: s.marks_per_question,
        splitMark: tp.split_mark || [],
        requires: sectionRequires(s),
        // { paper_question_id, quiz_id, display_order, question, marks }
        added: (prefilled[s.paper_section_id] || [])
          .slice()
          .sort((a, b) => a.display_order - b.display_order),
      };
    });
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [paperStatus] = useState(location.state?.paper_status || "DRAFT");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({}); // per-section: show added questions only when expanded

  // Browse state for the active section.
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ question_type: null, level: null });

  const sec = sections[activeIdx];
  const locked = paperStatus === "APPROVED";

  // Add-button behaviour mirrors the prototype: either-or adds exactly 2 as a pair.
  const selCount = selected.size;
  const secIsEO = sec?.pattern === "either-or";
  const addDisabled = busy || (secIsEO ? selCount !== 2 : selCount < 1);
  const addLabel = secIsEO ? "Add as Pair" : `Add Selected (${selCount})`;
  const footText = secIsEO
    ? selCount === 2
      ? "2 selected → 1 pair"
      : "Select exactly 2 to form an either-or pair"
    : selCount
      ? `${selCount} selected`
      : "Tick questions to add";

  useEffect(() => {
    if (!assessmentId) navigate("/questionPaperBuilder", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuestions = useCallback(async () => {
    if (!sec || !assessmentId) {
      setQuestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("BrowseQuestionsForSection", {
        assessment_id: assessmentId,
        paper_section_id: sec.paper_section_id,
        // Backend derives the marks constraint from paper_section_id; only send chosen filters.
        ...(filters.question_type ? { question_types: [filters.question_type] } : {}),
        ...(filters.level ? { level: [filters.level] } : {}),
        page,
        size: PAGE_SIZE,
      });
      setQuestions(res?.data?.data || []);
      setTotal(res?.data?.total || 0);
    } catch (e) {
      console.log("BrowseQuestionsForSection error:", e);
      setQuestions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sec, assessmentId, filters, page]);

  useEffect(() => {
    setSelected(new Set());
    fetchQuestions();
  }, [fetchQuestions]);

  // Reset paging/filters/selection when the active section changes.
  useEffect(() => {
    setPage(0);
    setFilters({ question_type: null, level: null });
    setSelected(new Set());
  }, [activeIdx]);

  const addedQuizIds = useMemo(() => {
    const set = new Set();
    sections.forEach((s) => s.added.forEach((q) => set.add(q.quiz_id)));
    return set;
  }, [sections]);

  // Question bank hides questions already added to any section; removing one brings it back.
  const visibleQuestions = questions.filter((q) => !q.already_in_paper && !addedQuizIds.has(q.quiz_id));

  const mutateSection = (idx, fn) =>
    setSections((prev) => prev.map((s, i) => (i === idx ? fn({ ...s, added: [...s.added] }) : s)));

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const handleAdd = async () => {
    const quiz_ids = [...selected];
    if (!quiz_ids.length) return;
    // Either-or sections are added two-at-a-time as an (a)/(b) pair.
    if (sec.pattern === "either-or" && quiz_ids.length !== 2) {
      return showSnackbar("error", "Select exactly 2 questions to form an either-or pair");
    }
    setBusy(true);
    try {
      const addBody = {
        paper_section_id: sec.paper_section_id,
        assessment_id: assessmentId,
        quiz_ids,
      };
      // Force the section's marks onto the picked questions (ignore quiz-bank marks).
      if (sec.marks != null) addBody.marks_override = quiz_ids.map(() => sec.marks);
      const res = await api.post("AddQuestionsToSection", addBody);
      if (res?.data?.status === "success") {
        const added = res?.data?.added || [];
        const byId = (id) => questions.find((q) => q.quiz_id === id);
        mutateSection(activeIdx, (s) => {
          added.forEach((a) => {
            const q = byId(a.quiz_id);
            s.added.push({
              paper_question_id: a.paper_question_id,
              quiz_id: a.quiz_id,
              display_order: a.display_order,
              question: q?.question || "",
              image: q?.image_url || q?.image || null,
              marks: q?.marks ?? s.marks,
            });
          });
          s.added.sort((x, y) => x.display_order - y.display_order);
          return s;
        });
        showSnackbar("success", res?.data?.message || `${added.length} question(s) added`);
        setSelected(new Set());
        fetchQuestions();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to add questions");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Failed to add questions");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (paperQuestionId) => {
    setBusy(true);
    try {
      const res = await api.post("RemoveQuestionFromSection", {
        paper_question_id: paperQuestionId,
        assessment_id: assessmentId,
      });
      if (res?.data?.status === "success") {
        mutateSection(activeIdx, (s) => {
          s.added = s.added
            .filter((q) => q.paper_question_id !== paperQuestionId)
            .map((q, i) => ({ ...q, display_order: i + 1 }));
          return s;
        });
        showSnackbar("success", res?.data?.message || "Question removed");
        fetchQuestions();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to remove question");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Failed to remove question");
    } finally {
      setBusy(false);
    }
  };

  // Either-or: remove both questions of a pair.
  const handleRemovePair = async (ids) => {
    setBusy(true);
    try {
      for (const id of ids.filter(Boolean)) {
        await api.post("RemoveQuestionFromSection", { paper_question_id: id, assessment_id: assessmentId });
      }
      mutateSection(activeIdx, (s) => {
        s.added = s.added.filter((q) => !ids.includes(q.paper_question_id)).map((q, i) => ({ ...q, display_order: i + 1 }));
        return s;
      });
      showSnackbar("success", "Pair removed");
      fetchQuestions();
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Failed to remove pair");
    } finally {
      setBusy(false);
    }
  };

  const handleReorder = async (qi, dir) => {
    const j = qi + dir;
    if (j < 0 || j >= sec.added.length) return;
    const reordered = [...sec.added];
    [reordered[qi], reordered[j]] = [reordered[j], reordered[qi]];
    const ordered_paper_question_ids = reordered.map((q) => q.paper_question_id);
    // Optimistic UI; revert on failure.
    mutateSection(activeIdx, (s) => {
      s.added = reordered.map((q, i) => ({ ...q, display_order: i + 1 }));
      return s;
    });
    try {
      const res = await api.post("ReorderQuestionsInSection", {
        paper_section_id: sec.paper_section_id,
        assessment_id: assessmentId,
        ordered_paper_question_ids,
      });
      if (res?.data?.status !== "success") {
        showSnackbar("error", res?.data?.message || "Failed to reorder");
        mutateSection(activeIdx, (s) => {
          s.added = sec.added;
          return s;
        });
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Failed to reorder");
      mutateSection(activeIdx, (s) => {
        s.added = sec.added;
        return s;
      });
    }
  };

  // Live preview built from the added questions.
  const previewPaper = useMemo(() => {
    const previewSections = sections.map((s) => {
      let items;
      if (s.pattern === "either-or") {
        items = [];
        for (let i = 0; i < s.added.length; i += 2) {
          items.push({
            a: { text: s.added[i]?.question, image: s.added[i]?.image },
            b: { text: s.added[i + 1]?.question, image: s.added[i + 1]?.image },
          });
        }
      } else {
        items = s.added.map((q) => ({ text: q.question, image: q.image }));
      }
      return {
        title: s.title,
        pattern: s.pattern,
        count: s.count,
        marks: s.marks,
        splitMark: s.splitMark || [],
        instructions: [],
        choice: null,
        items,
      };
    });
    return buildPaperFromSections(previewSections, headerData);
  }, [sections, headerData]);

  const totalRequired = sections.reduce((sum, s) => sum + s.requires, 0);
  const totalAdded = sections.reduce((sum, s) => sum + s.added.length, 0);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {/* Back */}
      <Grid sx={{ display: "flex", alignItems: "center", color: "#1c97e3", gap: "4px", cursor: "pointer", width: "40px", mb: 2 }} onClick={() => navigate("/questionPaperBuilder")}>
        <ArrowBackIcon sx={{ fontSize: "17px" }} />
        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
      </Grid>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, pb: 2, borderBottom: "1px solid #d1d1d1" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px", display: "inline-flex" }}>
            <AssignmentIcon sx={{ color: "white", fontSize: "32px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "26px", fontWeight: "bold" }}>Add Questions</Typography>
            <Typography sx={{ fontSize: "13px", color: "#8E9EBC" }}>
              {totalAdded}/{totalRequired} questions added · Status:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: locked ? "#16a34a" : "#d97706" }}>{paperStatus}</Box>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Two columns */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2, alignItems: "flex-start", pointerEvents: locked ? "none" : "auto", opacity: locked ? 0.9 : 1 }}>
        {/* LEFT — sections + added questions */}
        <Card sx={{ ...colSx, flex: "1 1 420px", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1.5 }}>Paper Sections</Typography>
          {sections.map((s, i) => {
            const isEO = s.pattern === "either-or";
            const filled = isEO ? Math.floor(s.added.length / 2) : s.added.length;
            const need = s.count;
            const ok = filled >= need;
            return (
              <Card key={i} onClick={() => setActiveIdx(i)} sx={{ p: 1.5, mb: 1.5, borderRadius: "10px", boxShadow: "none", cursor: "pointer", border: i === activeIdx ? "1px solid #1c97e3" : "1px solid #f1f3f6", ...(i === activeIdx ? { boxShadow: "0 0 0 2px rgba(28,151,227,0.1)" } : {}) }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>{s.title}</Typography>
                  <Chip size="small" label={isEO ? "Either-Or" : "List"} sx={{ ...tagBase, backgroundColor: "#eef2ff", color: "#4f46e5" }} />
                  <Chip size="small" label={`${s.marks} Marks`} sx={TAG.marks} />
                  <Chip size="small" label={`${filled}/${need} ${isEO ? "pairs" : "questions"}`} sx={{ ...tagBase, ml: "auto", backgroundColor: ok ? "#dcfce7" : "#fef3c7", color: ok ? "#16a34a" : "#b45309" }} />
                </Box>
                <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={filled === 0}
                    onClick={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
                    endIcon={expanded[i] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ textTransform: "none", borderRadius: "10px" }}
                  >
                    {expanded[i] ? "Hide" : "View"} Selected ({filled})
                  </Button>
                </Box>
                {expanded[i] && (
                  <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {isEO
                      ? Array.from({ length: Math.ceil(s.added.length / 2) }).map((_, pi) => {
                        const a = s.added[pi * 2];
                        const b = s.added[pi * 2 + 1];
                        return (
                          <Box key={a?.paper_question_id ?? pi} sx={{ backgroundColor: "#fafbfc", border: "1px solid #f1f3f6", borderRadius: "8px", p: 0.75 }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 40 }}>{pi + 1}. a)</Typography>
                              <Typography sx={{ flex: 1, fontSize: "13px" }}>{a?.question}</Typography>
                              {i === activeIdx && (
                                <IconButton size="small" onClick={() => handleRemovePair([a?.paper_question_id, b?.paper_question_id])} disabled={busy}><CloseIcon sx={{ fontSize: 16, color: "#d32f2f" }} /></IconButton>
                              )}
                            </Box>
                            <Typography sx={{ textAlign: "center", fontStyle: "italic", color: "#8e9ebc", fontSize: "12px" }}>(or)</Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 40 }}>b)</Typography>
                              <Typography sx={{ flex: 1, fontSize: "13px" }}>{b?.question}</Typography>
                            </Box>
                          </Box>
                        );
                      })
                      : s.added.map((q, qi) => (
                        <Box key={q.paper_question_id} sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fafbfc", border: "1px solid #f1f3f6", borderRadius: "8px", p: 0.75 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 26 }}>{qi + 1}.</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13px" }}>{q.question}</Typography>
                          {i === activeIdx && (
                            <>
                              <IconButton size="small" onClick={() => handleReorder(qi, -1)} disabled={qi === 0 || busy}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={() => handleReorder(qi, 1)} disabled={qi === s.added.length - 1 || busy}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={() => handleRemove(q.paper_question_id)} disabled={busy}><CloseIcon sx={{ fontSize: 16, color: "#d32f2f" }} /></IconButton>
                            </>
                          )}
                        </Box>
                      ))}
                  </Box>
                )}
              </Card>
            );
          })}
        </Card>

        {/* RIGHT — question bank browser */}
        <Card sx={{ ...colSx, flex: "1 1 420px", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1 }}>{`${getLabel("QUESTION_BANK")}`}</Typography>
          {sec && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", mb: 1.5, fontSize: "12px", color: "#8e9ebc" }}>
              <b>{sec.title}</b> — pick:
              <Chip size="small" label={secIsEO ? "Either-Or" : "List"} sx={{ ...tagBase, backgroundColor: "#eef2ff", color: "#4f46e5" }} />
              <Chip size="small" label={`${sec.marks} Marks`} sx={TAG.marks} />
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
            <Autocomplete
              size="small" sx={{ flex: "1 1 160px" }} options={QUESTION_TYPE_OPTIONS} value={filters.question_type}
              onChange={(_, v) => { setPage(0); setFilters((f) => ({ ...f, question_type: v })); }}
              renderInput={(params) => <TextField {...params} placeholder="Question Type" sx={commonTextFieldStyle} />}
            />
            <Autocomplete
              size="small" sx={{ flex: "1 1 160px" }} options={LEVEL_OPTIONS} value={filters.level}
              onChange={(_, v) => { setPage(0); setFilters((f) => ({ ...f, level: v })); }}
              renderInput={(params) => <TextField {...params} placeholder="Level" sx={commonTextFieldStyle} />}
            />
          </Box>

          <Box sx={{ maxHeight: 460, overflow: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", p: 3 }}><CircularProgress size={24} /></Box>
            ) : visibleQuestions.length ? (
              visibleQuestions.map((q) => {
                return (
                  <Box key={q.quiz_id} sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1, border: "1px solid #f1f3f6", borderRadius: "8px" }}>
                    <Checkbox size="small" checked={selected.has(q.quiz_id)} onChange={() => toggleSelect(q.quiz_id)} sx={{ p: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "13px" }}>{q.question}</Typography>
                      {(q.image_url || q.image) && (
                        <Box
                          component="img"
                          src={q.image_url || q.image}
                          alt="question"
                          sx={{ display: "block", maxWidth: "100%", maxHeight: 160, mt: 0.5, borderRadius: "8px", border: "1px solid #f1f3f6", objectFit: "contain" }}
                        />
                      )}
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                        {q.question_type && <Chip size="small" label={q.question_type} sx={TAG.type} />}
                        {q.level && <Chip size="small" label={q.level} sx={TAG.level} />}
                        <Chip size="small" label={`${q.marks} Marks`} sx={TAG.marks} />
                        {q.blooms_level && <Chip size="small" label={q.blooms_level} sx={TAG.bloom} />}
                        {q.subcategory && <Chip size="small" label={q.subcategory} sx={TAG.sub} />}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ color: "#8e9ebc", textAlign: "center", p: 3 }}>No questions found for this section.</Typography>
            )}
          </Box>

          {/* Pagination + add */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button size="small" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} sx={{ textTransform: "none", minWidth: 0 }}>Prev</Button>
              <Typography sx={{ fontSize: "12px", color: "#8e9ebc" }}>
                {total ? `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}` : "0"}
              </Typography>
              <Button size="small" disabled={(page + 1) * PAGE_SIZE >= total || loading} onClick={() => setPage((p) => p + 1)} sx={{ textTransform: "none", minWidth: 0 }}>Next</Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: "12px", color: "#8e9ebc" }}>{footText}</Typography>
              <Button variant="contained" size="small" disabled={addDisabled} onClick={handleAdd} sx={{ textTransform: "none", borderRadius: "10px" }}>
                {addLabel}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Live preview */}
      <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #d1d1d1" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: "bold", mb: 2 }}>{`${getLabel('QUESTION_PAPER')} Preview`}</Typography>
        <QuestionPaperTemplate data={previewPaper} />
      </Box>
    </Box>
  );
};

export default AddQuestionsToPaper;
